import {Injectable} from '@angular/core';

import {Feature} from 'ol';
import {Fill, Stroke, Style} from 'ol/style';
import {GeoJSON, TopoJSON} from 'ol/format';
import {Geometry} from 'ol/geom';
import {Vector as VectorLayer} from 'ol/layer';
import {Vector as VectorSource} from 'ol/source';

import {
  HsConfig,
  HsEventBusService,
  HsLanguageService,
  HsLayoutService,
  HsPanelContainerService,
  HsSidebarService,
  HsToastService,
} from 'hslayers-ng';

import {AdjusterAdvancedPanelComponent} from './adjuster/adjuster-advanced-panel/adjuster-advanced-panel.component';
import {AdjusterEventService} from './adjuster/adjuster-event.service';
import {AdjusterLegendService} from './adjuster/adjuster-legend.service';
import {AdjusterService} from './adjuster/adjuster.service';
import {AdjusterSimplePanelComponent} from './adjuster/adjuster-simple-panel/adjuster-simple-panel.component';
import {
  AppConfig,
  DEFAULT_OPACITY,
  nuts2Layer,
  nuts3IndexLayer,
} from './app.config';

@Injectable({providedIn: 'root'})
export class AppService {
  pilotsColor = 'rgba(29, 148, 29, 0.2)';
  pilotsStyle = new Style({
    stroke: new Stroke({
      color: '#1d941d',
      width: 1.5,
    }),
    fill: new Fill({
      color: this.pilotsColor,
    }),
  });
  pilotRegions = new VectorLayer({
    properties: {
      title: 'Polirural Pilot Regions',
      editor: {editable: false},
    },
    source: new VectorSource({
      format: new GeoJSON(),
      // eslint-disable-next-line @typescript-eslint/no-var-requires
      url: require('./data/pilot_regions.geojson').default,
    }),
    visible: true,
    style: this.pilotsStyle,
  });
  constructor(
    public adjusterService: AdjusterService,
    public adjusterEventService: AdjusterEventService,
    public adjusterLegendService: AdjusterLegendService,
    public hsConfig: HsConfig,
    public hsEventBus: HsEventBusService,
    public hsLanguageService: HsLanguageService,
    public hsLayoutService: HsLayoutService,
    public hsPanelContainerService: HsPanelContainerService,
    public hsSidebarService: HsSidebarService,
    public hsToastService: HsToastService
  ) {
    this.hsSidebarService.addButton({
      panel: 'adjuster-simple',
      module: 'pra.adjuster',
      order: 0,
      title: () =>
        this.hsLanguageService.getTranslation('ADJUSTER.adjustFactorsSimple'),
      description: () =>
        this.hsLanguageService.getTranslation(
          'ADJUSTER.adjustFactorsSimpleDescription'
        ),
      icon: 'icon-analytics-piechart',
    });
    this.hsPanelContainerService.create(AdjusterSimplePanelComponent, {});
    this.hsSidebarService.addButton({
      panel: 'adjuster-advanced',
      module: 'pra.adjuster',
      order: 0,
      title: () =>
        this.hsLanguageService.getTranslation('ADJUSTER.adjustFactors'),
      description: () =>
        this.hsLanguageService.getTranslation(
          'ADJUSTER.adjustFactorsDescription'
        ),
      icon: 'icon-settingsandroid',
    });
    this.hsPanelContainerService.create(AdjusterAdvancedPanelComponent, {});
    this.prepareLayers();
    this.adjusterEventService.loaded.subscribe(({success}) => {
      if (success) {
        this.adjusterLegendService.refreshColorPalette(
          this.adjusterService.numberOfClusters
        );
      }
    });
    this.hsEventBus.layoutLoads.subscribe(() => {
      this.init();
    });
    this.hsEventBus.olMapLoads.subscribe(() => {
      this.ensureLayerIsLoaded(nuts3IndexLayer);
      for (const method of this.adjusterService.methods) {
        this.ensureLayerIsLoaded(method.layer);
      }
    });
    /* For debugging only */
    /*this.hsEventBus.olMapLoads.subscribe((map) => {
      map.on('click', this.debugMe);
    });*/
    /*this.adjusterEventService.methodChanged.subscribe((method) => {
      //TODO prettify this
      this.nuts3ClustersLayer.set(
        'title',
        `NUTS3 regions: ${method.replaceAll(/\((.+?)\)/g, '')} Clusters`
      );
      this.nuts3ClustersLayer.get('popUp').attributes[2].attribute = method;
      this.nuts3ClustersLayer.setStyle(
        this.generateStyle(this.adjusterService.method)
      );
    });*/
  }

  init(): void {
    this.hsLayoutService.setDefaultPanel('adjuster-simple');
  }

  /**
   * Create separate layer fo each clustering method and add it to default_layers
   */
  prepareLayers(): void {
    for (const method of this.adjusterService.methods) {
      method.layer = new VectorLayer({
        properties: {
          editor: {editable: false},
          editable: false,
          //queryable: false,
          autoLegend: false,
          title: `NUTS3 regions: ${method.name.replace(
            /\((.+?)\)/g,
            ''
          )} clusters`,
          popUp: {
            attributes: [
              {
                attribute: method.codename,
                label: 'Cluster ID',
              },
            ],
          },
        },
        source: new VectorSource({
          //features: new GeoJSON().readFeatures(nuts3).map((f) => f.clone()),
          format: new TopoJSON({dataProjection: 'EPSG:3857'}),
          // eslint-disable-next-line @typescript-eslint/no-var-requires
          url: require('./data/NUTS_RG_20M_2016_3857_LEVL_3.topojson').default,
          overlaps: false,
        }),
        opacity: DEFAULT_OPACITY,
        visible: true,
        style: this.generateStyle(method.codename),
      });
      method.layer.getSource().on('featuresloadend', () => {
        this.adjusterEventService.layerReady.next({name: method.codename});
      });
      method.layer.getSource().on('featuresloaderror', (evt) => {
        this.hsToastService.createToastPopupMessage(
          'Error loading layer',
          // eslint-disable-next-line prettier/prettier
          `The layer ${
            (evt.target as VectorSource<Geometry>).get('title')
            // eslint-disable-next-line prettier/prettier
          } could not be loaded`,
          {toastStyleClasses: 'bg-danger text-light'}
        );
        console.log('evil shit');
      });
      AppConfig.default_layers.push(method.layer as any);
    }
    /* The order of pushes matter! */
    AppConfig.default_layers.push(nuts3IndexLayer as any);
    nuts3IndexLayer
      .getSource()
      .on('featuresloadend', () =>
        this.adjusterEventService.layerReady.next({name: 'index'})
      );
    (nuts3IndexLayer.getSource() as any).legend_categories =
      this.adjusterLegendService.createIndexLegend();
    AppConfig.default_layers.push(nuts2Layer as any);
    AppConfig.default_layers.push(this.pilotRegions as any);
    this.pilotRegions.set('popUp', {
      attributes: [
        {attribute: 'NUTS_NAME', label: 'Region name'},
        //TODO: Uncomment when implemented in the rural-attractiveness-service
        /*{
          attribute: 'NUTS_ID',
          label: 'Pilot region details',
          displayFunction: (x) => {
            return `<a href="${this.serviceUrl}${x}" target="_blank">in a new page</a>.`;
          },
        },*/
      ],
    });
    (this.pilotRegions.getSource() as any).legend_categories = [
      {
        color: this.pilotsColor,
        name: 'Polirural pilot region',
      },
    ];
    this.pilotRegions.set('queryable', false);
    this.hsConfig.update(AppConfig);
  }

  /**
   * Layer must be turned visible so it is loaded into the map.
   * This method ensures that layers critical for further computations
   * are for at least a moment turned visible and thus loaded properly.
   * @param {VectorLayer} layer One of layers used to calculate index or clusters
   */
  ensureLayerIsLoaded(layer: VectorLayer<VectorSource<Geometry>>): void {
    const layerVisible = layer.getVisible();
    if (!layerVisible) {
      layer.setVisible(true);
      setTimeout(() => layer.setVisible(false), 10);
    }
  }

  /**
   * @description Function factory for generating style functions based on different clustering methods
   * @param {string} method currently selected method
   * @returns {function} style function
   */
  private generateStyle(method: string) {
    return (feature: Feature<Geometry>): Style => {
      if (isNaN(feature.get(method))) {
        return new Style({
          fill: new Fill({
            color: '#FFF',
          }),
          stroke: new Stroke({
            color: '#3399CC',
            width: 0.25,
          }),
        });
      } else {
        return new Style({
          fill: new Fill({
            color:
              this.adjusterLegendService.colorPalette[feature.get(method) - 1],
          }),
          stroke: new Stroke({
            color: '#FFF',
            width: 0.25,
          }),
        });
      }
    };
  }

  private debugMe(e) {
    console.log(e);
    const feats = e.map.getFeaturesAtPixel(e.pixel);
    console.log(feats);
  }
}
